import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAdminContent, deletePost, deleteStrain, exportPostsSnapshot } from "@/lib/admin-content.functions";
import {
  listBenzosAdmin, deleteBenzo, toggleBenzoPublished, getBenzo, restoreBenzo,
  listOpioidsAdmin, deleteOpioid, toggleOpioidPublished, getOpioid, restoreOpioid,
  listSubstancesAdmin,
} from "@/lib/admin-substances.functions";
import { LogOut, Plus, Pencil, Search, FlaskConical, Network, Trash2 } from "lucide-react";

import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/_admin/admin")({
  component: AdminRoute,
});

function AdminRoute() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isDashboard = pathname === "/admin" || pathname === "/admin/";

  return isDashboard ? <AdminPage /> : <Outlet />;
}

const cardStyle: React.CSSProperties = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-purple)",
  borderRadius: "var(--radius-sm)",
  padding: "1.25rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--border-purple)",
  borderRadius: 6,
  padding: "0.35rem 0.6rem 0.35rem 1.8rem",
  color: "var(--text-primary)",
  fontSize: "0.8rem",
};

type DeleteTarget =
  | { kind: "post"; id: string; title: string }
  | { kind: "strain"; id: string; name: string }
  | { kind: "benzo"; id: string; name: string }
  | { kind: "opioid"; id: string; name: string };

function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState<string | null>(null);
  const fetchList = useServerFn(listAdminContent);
  const fetchBenzos = useServerFn(listBenzosAdmin);
  const fetchOpioids = useServerFn(listOpioidsAdmin);
  const fetchSubs = useServerFn(listSubstancesAdmin);
  const doDeletePost = useServerFn(deletePost);
  const doDeleteStrain = useServerFn(deleteStrain);
  const doDeleteBenzo = useServerFn(deleteBenzo);
  const doDeleteOpioid = useServerFn(deleteOpioid);
  const doGetBenzo = useServerFn(getBenzo);
  const doGetOpioid = useServerFn(getOpioid);
  const doRestoreBenzo = useServerFn(restoreBenzo);
  const doRestoreOpioid = useServerFn(restoreOpioid);
  const doToggleBenzo = useServerFn(toggleBenzoPublished);
  const doToggleOpioid = useServerFn(toggleOpioidPublished);
  const doExportSnapshot = useServerFn(exportPostsSnapshot);
  const [target, setTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);


  const [q, setQ] = useState({ blog: "", hr: "", med: "", benzo: "", opioid: "", subs: "" });
  const [filterBenzoPub, setFilterBenzoPub] = useState<"all" | "pub" | "draft">("all");
  const [filterOpioidPub, setFilterOpioidPub] = useState<"all" | "pub" | "draft">("all");
  const [filterSubCat, setFilterSubCat] = useState<string>("");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-content"],
    queryFn: () => fetchList(),
  });

  const { data: benzos } = useQuery({
    queryKey: ["admin-substances", "benzos"],
    queryFn: () => fetchBenzos(),
  });
  const { data: opioids } = useQuery({
    queryKey: ["admin-substances", "opioids"],
    queryFn: () => fetchOpioids(),
  });
  const { data: subsData } = useQuery({
    queryKey: ["admin-substances", "list"],
    queryFn: () => fetchSubs(),
  });

  async function handleLogout() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  async function confirmDelete() {
    if (!target) return;
    setDeleting(true);
    try {
      if (target.kind === "post") {
        await doDeletePost({ data: { id: target.id } });
        toast.success(`Eintrag "${target.title}" geloescht`);
      } else if (target.kind === "strain") {
        await doDeleteStrain({ data: { id: target.id } });
        toast.success(`Strain "${target.name}" geloescht`);
      } else if (target.kind === "benzo") {
        // Snapshot for undo
        let snapshot: any = null;
        try { snapshot = await doGetBenzo({ data: { id: target.id } }); } catch { /* best-effort */ }
        await doDeleteBenzo({ data: { id: target.id } });
        toast.success(`Benzo "${target.name}" geloescht`, {
          duration: 6000,
          action: snapshot ? {
            label: "Rueckgaengig",
            onClick: async () => {
              try {
                await doRestoreBenzo({ data: { row: snapshot } });
                queryClient.invalidateQueries({ queryKey: ["admin-substances"] });
                queryClient.invalidateQueries({ queryKey: ["public-benzos"] });
                toast.success("Wiederhergestellt");
              } catch (e: any) {
                toast.error("Wiederherstellen fehlgeschlagen", { description: e?.message });
              }
            },
          } : undefined,
        });
      } else if (target.kind === "opioid") {
        let snapshot: any = null;
        try { snapshot = await doGetOpioid({ data: { id: target.id } }); } catch { /* best-effort */ }
        await doDeleteOpioid({ data: { id: target.id } });
        toast.success(`Opioid "${target.name}" geloescht`, {
          duration: 6000,
          action: snapshot ? {
            label: "Rueckgaengig",
            onClick: async () => {
              try {
                await doRestoreOpioid({ data: { row: snapshot } });
                queryClient.invalidateQueries({ queryKey: ["admin-substances"] });
                queryClient.invalidateQueries({ queryKey: ["public-opioids"] });
                toast.success("Wiederhergestellt");
              } catch (e: any) {
                toast.error("Wiederherstellen fehlgeschlagen", { description: e?.message });
              }
            },
          } : undefined,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["admin-content"] });
      queryClient.invalidateQueries({ queryKey: ["admin-substances"] });
      queryClient.invalidateQueries({ queryKey: ["public-benzos"] });
      queryClient.invalidateQueries({ queryKey: ["public-opioids"] });
      setTarget(null);
    } catch (e: any) {
      toast.error("Loeschen fehlgeschlagen", { description: e?.message ?? String(e) });
    } finally {
      setDeleting(false);
    }
  }


  async function togglePublished(kind: "benzo" | "opioid", id: string, current: boolean) {
    const next = !current;
    try {
      if (kind === "benzo") await doToggleBenzo({ data: { id, published: next } });
      else await doToggleOpioid({ data: { id, published: next } });
      toast.success(next ? "Veroeffentlicht" : "Auf Entwurf gesetzt");
      queryClient.invalidateQueries({ queryKey: ["admin-substances"] });
      queryClient.invalidateQueries({ queryKey: ["public-benzos"] });
      queryClient.invalidateQueries({ queryKey: ["public-opioids"] });
    } catch (e: any) {
      toast.error("Fehler", { description: e?.message ?? String(e) });
    }
  }

  const filteredBenzos = useMemo(() => {
    const list = benzos ?? [];
    const term = q.benzo.toLowerCase();
    return list.filter((b: any) => {
      if (filterBenzoPub === "pub" && !b.published) return false;
      if (filterBenzoPub === "draft" && b.published) return false;
      if (!term) return true;
      return b.substance.toLowerCase().includes(term) || (b.compound_class ?? "").toLowerCase().includes(term);
    });
  }, [benzos, q.benzo, filterBenzoPub]);

  const filteredOpioids = useMemo(() => {
    const list = opioids ?? [];
    const term = q.opioid.toLowerCase();
    return list.filter((o: any) => {
      if (filterOpioidPub === "pub" && !o.published) return false;
      if (filterOpioidPub === "draft" && o.published) return false;
      if (!term) return true;
      return o.substance.toLowerCase().includes(term) || (o.compound_class ?? "").toLowerCase().includes(term);
    });
  }, [opioids, q.opioid, filterOpioidPub]);

  const filteredBlog = useMemo(() => {
    const list = data?.blog ?? [];
    const term = q.blog.toLowerCase();
    return term ? list.filter((p: any) => p.title.toLowerCase().includes(term)) : list;
  }, [data?.blog, q.blog]);

  const filteredHR = useMemo(() => {
    const list = data?.harmReduction ?? [];
    const term = q.hr.toLowerCase();
    return term ? list.filter((p: any) => p.title.toLowerCase().includes(term)) : list;
  }, [data?.harmReduction, q.hr]);

  const filteredMed = useMemo(() => {
    const list = data?.strains ?? [];
    const term = q.med.toLowerCase();
    return term ? list.filter((s: any) => s.name.toLowerCase().includes(term)) : list;
  }, [data?.strains, q.med]);

  const filteredSubs = useMemo(() => {
    const list = subsData?.substances ?? [];
    const term = q.subs.toLowerCase();
    return list.filter((s: any) => {
      if (filterSubCat && String(s.category_id ?? "") !== filterSubCat) return false;
      if (!term) return true;
      return s.name.toLowerCase().includes(term);
    });
  }, [subsData, q.subs, filterSubCat]);

  async function handleExportSnapshot() {
    setExporting(true);
    try {
      const snapshot = await doExportSnapshot();
      const json = JSON.stringify(snapshot, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "posts.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Snapshot exportiert");
    } catch (e: any) {
      toast.error("Export fehlgeschlagen", { description: e?.message ?? String(e) });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={{ maxWidth: 1200, margin: "2rem auto", padding: "0 1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ color: "var(--accent-cyan)", margin: 0 }}>Admin</h1>
          <p style={{ color: "var(--text-muted)", margin: "0.25rem 0 0", fontSize: "0.85rem" }}>
            Eingeloggt als {email ?? "…"}
          </p>
        </div>
        <button onClick={handleLogout} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "0.4rem 0.9rem", background: "transparent", color: "var(--text-muted)",
          border: "1px solid var(--border-purple)", borderRadius: "var(--radius-sm)", cursor: "pointer",
        }}>
          <LogOut size={14} /> Logout
        </button>
      </div>

      {isLoading && <p style={{ color: "var(--text-muted)" }}>Lade Inhalte…</p>}
      {error && <p style={{ color: "#ef4444" }}>Fehler: {(error as Error).message}</p>}

      <div style={{
        display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
        padding: "0.85rem 1rem", marginBottom: "1.25rem",
        background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-purple)",
        borderRadius: "var(--radius-sm)",
      }}>
        <button
          onClick={handleExportSnapshot}
          disabled={exporting}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "0.45rem 0.9rem", background: "var(--accent-bg)",
            color: "var(--accent-cyan)", border: "1px solid var(--accent-border)",
            borderRadius: "var(--radius-sm)", cursor: exporting ? "wait" : "pointer",
            fontSize: "0.85rem", fontWeight: 600, opacity: exporting ? 0.6 : 1,
          }}
        >
          {exporting ? "Exportiere…" : "Fallback-Snapshot exportieren"}
        </button>
        <span style={{ color: "var(--text-muted)", fontSize: "0.78rem", lineHeight: 1.4 }}>
          Dieser Snapshot dient als Fallback, falls die Datenbank nicht erreichbar ist.
          Die exportierte Datei nach <code>src/data/posts.json</code> commiten.
        </span>
      </div>

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: "1rem" }}>
          <Section
            title="Blog"
            newHref="/admin/blog/new"
            search={<SearchBox value={q.blog} onChange={(v) => setQ((s) => ({ ...s, blog: v }))} />}
            items={filteredBlog.map((p: any) => ({
              key: p.id,
              primary: p.title,
              secondary: `${p.published ? "✓" : "–"} · ${p.published_date}`,
              editEl: <EditLink to="/admin/blog/$id/edit" params={{ id: p.id }} />,
              onDelete: () => setTarget({ kind: "post", id: p.id, title: p.title }),
            }))}
          />
          <Section
            title="Harm Reduction"
            newHref="/admin/harm-reduction/new"
            search={<SearchBox value={q.hr} onChange={(v) => setQ((s) => ({ ...s, hr: v }))} />}
            items={filteredHR.map((p: any) => ({
              key: p.id,
              primary: p.title,
              secondary: `${p.published ? "✓" : "–"} · ${p.published_date}`,
              editEl: <EditLink to="/admin/harm-reduction/$id/edit" params={{ id: p.id }} />,
              onDelete: () => setTarget({ kind: "post", id: p.id, title: p.title }),
            }))}
          />
          <Section
            title="MedCan"
            newHref="/admin/medcan/new"
            search={<SearchBox value={q.med} onChange={(v) => setQ((s) => ({ ...s, med: v }))} />}
            items={filteredMed.map((s: any) => ({
              key: s.id,
              primary: s.name,
              secondary: `${s.dominance} · ${s.strain_batches?.length ?? 0} Batch(es)`,
              editEl: <EditLink to="/admin/medcan/$id/edit" params={{ id: s.id }} />,
              onDelete: () => setTarget({ kind: "strain", id: s.id, name: s.name }),
            }))}
          />
          <Section
            title="Benzodiazepine"
            newHref="/admin/benzo/new"
            search={
              <>
                <SearchBox value={q.benzo} onChange={(v) => setQ((s) => ({ ...s, benzo: v }))} />
                <select value={filterBenzoPub} onChange={(e) => setFilterBenzoPub(e.target.value as any)} style={{ ...searchInputStyle, padding: "0.35rem 0.5rem", flex: "0 0 auto" }}>
                  <option value="all">Alle</option>
                  <option value="pub">Veroeffentlicht</option>
                  <option value="draft">Entwurf</option>
                </select>
              </>
            }
            items={filteredBenzos.map((b: any) => ({
              key: b.id,
              primary: b.substance,
              secondary: `${b.compound_class || "—"}`,
              published: b.published,
              onTogglePublish: () => togglePublished("benzo", b.id, b.published),
              editEl: <EditLink to="/admin/benzo/$id/edit" params={{ id: b.id }} />,
              onDelete: () => setTarget({ kind: "benzo", id: b.id, name: b.substance }),
            }))}
          />
          <Section
            title="Opioide"
            newHref="/admin/opioide/new"
            search={
              <>
                <SearchBox value={q.opioid} onChange={(v) => setQ((s) => ({ ...s, opioid: v }))} />
                <select value={filterOpioidPub} onChange={(e) => setFilterOpioidPub(e.target.value as any)} style={{ ...searchInputStyle, padding: "0.35rem 0.5rem", flex: "0 0 auto" }}>
                  <option value="all">Alle</option>
                  <option value="pub">Veroeffentlicht</option>
                  <option value="draft">Entwurf</option>
                </select>
              </>
            }
            items={filteredOpioids.map((o: any) => ({
              key: o.id,
              primary: o.substance,
              secondary: `${o.compound_class || "—"}`,
              published: o.published,
              onTogglePublish: () => togglePublished("opioid", o.id, o.published),
              editEl: <EditLink to="/admin/opioide/$id/edit" params={{ id: o.id }} />,
              onDelete: () => setTarget({ kind: "opioid", id: o.id, name: o.substance }),
            }))}
          />
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>Matrix-Substanzen</h2>
              <Link to="/admin/substances/new" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.85rem" }}>
                <Plus size={14} /> Neu
              </Link>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <SearchBox value={q.subs} onChange={(v) => setQ((s) => ({ ...s, subs: v }))} />
              <select value={filterSubCat} onChange={(e) => setFilterSubCat(e.target.value)} style={{ ...searchInputStyle, padding: "0.35rem 0.5rem", flex: "0 0 auto" }}>
                <option value="">Alle Kategorien</option>
                {(subsData?.categories ?? []).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.label_de ?? c.key}</option>
                ))}
              </select>
            </div>
            {filteredSubs.length === 0 ? (
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>Keine Eintraege.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
                {filteredSubs.map((s: any) => (
                  <li key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.45rem 0.6rem", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                    <span style={{ color: "var(--text-primary)", fontSize: "0.88rem" }}>{s.name}</span>
                    <Link to="/admin/substances/$id/edit" params={{ id: s.id }} style={{ color: "var(--accent-cyan)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.78rem" }}>
                      <FlaskConical size={12} /> Profil/Routes/Effects
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>Interaktionsmatrix</h2>
              <Link to="/admin/matrix" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.85rem" }}>
                <Network size={14} /> Oeffnen
              </Link>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>
              Substanzen anlegen und Interaktionspaare pflegen mit Suche, Filter, Delete/Publish-Toggle und Live-Vorschau.
            </p>
          </div>
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>Quellen</h2>
              <Link to="/admin/sources" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.85rem" }}>
                <Network size={14} /> Oeffnen
              </Link>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>
              Belege anlegen und Seiten sowie Substanzkombinationen zuordnen. Ersetzt die manuelle Pflege in sources.ts.
            </p>
          </div>
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>KI-Fragen zur Matrix</h2>
              <Link to="/admin/ai-queries" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.85rem" }}>
                <Network size={14} /> Oeffnen
              </Link>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>
              Gestellte Fragen zu Kombinationen mit Paar, Datum, Status und erzeugter Antwort.
            </p>
          </div>

        </div>
      )}

      <AlertDialog open={target !== null} onOpenChange={(o) => !o && !deleting && setTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Wirklich loeschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {target?.kind === "strain"
                ? <>„{target.name}" wird geloescht. <strong>Alle zugehoerigen Batches werden mitgeloescht.</strong> Diese Aktion kann nicht rueckgaengig gemacht werden.</>
                : target?.kind === "post"
                  ? <>„{target.title}" wird geloescht. Diese Aktion kann nicht rueckgaengig gemacht werden.</>
                  : target?.kind === "benzo" || target?.kind === "opioid"
                    ? <>„{target.name}" wird geloescht. Diese Aktion kann nicht rueckgaengig gemacht werden.</>
                    : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}>
              {deleting ? "Loesche…" : "Loeschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: "relative", flex: 1, display: "flex" }}>
      <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Suchen…"
        style={searchInputStyle}
      />
    </div>
  );
}

function EditLink({ to, params }: { to: any; params: any }) {
  return (
    <Link to={to} params={params} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.75rem",
    }}>
      <Pencil size={12} /> Edit
    </Link>
  );
}

interface SectionItem {
  key: string;
  primary: string;
  secondary: string;
  editEl: React.ReactNode;
  onDelete: () => void;
  published?: boolean;
  onTogglePublish?: () => void;
}

function Section({
  title, newHref, items, search,
}: {
  title: string;
  newHref: string;
  items: SectionItem[];
  search?: React.ReactNode;
}) {
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>{title}</h2>
        <Link to={newHref as any} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.85rem",
        }}>
          <Plus size={14} /> Neu
        </Link>
      </div>
      {search && <div style={{ display: "flex", gap: 6 }}>{search}</div>}
      {items.length === 0 ? (
        <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>Keine Eintraege.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
          {items.map((it) => (
            <li key={it.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0.6rem", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <span style={{ color: "var(--text-primary)", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.primary}</span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{it.secondary}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginLeft: 8 }}>
                {it.onTogglePublish && (
                  <button
                    onClick={it.onTogglePublish}
                    title={it.published ? "Auf Entwurf setzen" : "Veroeffentlichen"}
                    style={{
                      background: it.published ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${it.published ? "rgba(16,185,129,0.5)" : "rgba(255,255,255,0.15)"}`,
                      color: it.published ? "#34d399" : "var(--text-muted)",
                      cursor: "pointer", borderRadius: 12,
                      padding: "2px 8px", fontSize: "0.7rem", fontWeight: 600,
                    }}
                  >
                    {it.published ? "Live" : "Entwurf"}
                  </button>
                )}
                {it.editEl}
                <button onClick={it.onDelete} title="Loeschen" style={{
                  background: "transparent", border: "none", color: "#ef4444",
                  cursor: "pointer", display: "inline-flex", alignItems: "center",
                }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
